<template>
  <div class="contact-page" :dir="$i18n.locale === 'ar' ? 'rtl' : 'ltr'">
    <!-- Hero Section -->
    <section class="relative py-24 bg-[#0f4640] overflow-hidden">
      <div class="container mx-auto px-4">
        <div class="flex flex-col items-start max-w-4xl" :class="$i18n.locale === 'ar' ? 'text-right' : 'text-left'">
          <div class="flex flex-col gap-6 relative z-10">
            <h1 class="hero-title" :class="$i18n.locale === 'ar' ? 'flex flex-col gap-2' : ''">
              <template v-if="$i18n.locale === 'ar'">
                <span class="text-8xl font-bold text-white">اتصل</span>
                <span class="text-7xl font-bold text-white">بنا</span>
              </template>
              <template v-else>
                <span class="text-7xl font-bold text-white">Contact Us</span>
              </template>
            </h1>
            <h2 class="text-2xl text-white/90">
              {{ $t('contact.hero.subtitle') }}
            </h2>
            <p class="text-xl text-white/80 leading-relaxed max-w-2xl">
              {{ $t('contact.hero.description') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Decorative Elements -->
      <div class="absolute inset-0 pointer-events-none">
        <!-- Background Pattern -->
        <div class="absolute inset-0 opacity-10">
          <div class="absolute top-0 right-0 w-96 h-96 bg-white rounded-full filter blur-3xl transform translate-x-1/2 -translate-y-1/2"></div>
          <div class="absolute bottom-0 left-0 w-96 h-96 bg-[#1f9285] rounded-full filter blur-3xl transform -translate-x-1/2 translate-y-1/2"></div>
        </div>
        
        <!-- Dots Pattern -->
        <div class="absolute inset-0 opacity-5" 
          style="background-image: radial-gradient(circle at 1px 1px, white 1px, transparent 0); background-size: 40px 40px;">
        </div>
      </div>
    </section>

    <!-- Contact Form and Info Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <!-- Contact Form -->
          <div>
            <h2 class="text-3xl font-bold text-[#0f4640] mb-8">
              أرسل لنا رسالة
            </h2>
            <form @submit.prevent="handleSubmit" class="space-y-6">
              <div class="grid grid-cols-1 gap-6">
                <div>
                  <label class="block text-base font-medium text-gray-700 mb-2">
                    الاسم الكامل
                  </label>
                  <input 
                    type="text" 
                    v-model="form.name"
                    placeholder="أدخل اسمك الكامل"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#0f4640] focus:ring-1 focus:ring-[#0f4640] transition-all"
                    required
                  >
                </div>
                <div>
                  <label class="block text-base font-medium text-gray-700 mb-2">
                    البريد الإلكتروني
                  </label>
                  <input 
                    type="email" 
                    v-model="form.email"
                    placeholder="أدخل بريدك الإلكتروني"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#0f4640] focus:ring-1 focus:ring-[#0f4640] transition-all"
                    required
                  >
                </div>
                <div>
                  <label class="block text-base font-medium text-gray-700 mb-2">
                    رقم الهاتف
                  </label>
                  <input 
                    type="tel" 
                    v-model="form.phone"
                    placeholder="أدخل رقم هاتفك"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#0f4640] focus:ring-1 focus:ring-[#0f4640] transition-all"
                  >
                </div>
                <div>
                  <label class="block text-base font-medium text-gray-700 mb-2">
                    الرسالة
                  </label>
                  <textarea 
                    v-model="form.message"
                    placeholder="اكتب رسالتك هنا..."
                    rows="5"
                    class="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#0f4640] focus:ring-1 focus:ring-[#0f4640] transition-all"
                    required
                  ></textarea>
                </div>
              </div>

              <button 
                type="submit"
                class="w-full bg-[#0f4640] hover:bg-[#1f9285] text-white font-medium py-4 px-8 rounded-lg transition-all duration-300"
              >
                إرسال الرسالة
              </button>
            </form>
          </div>

          <!-- Contact Information -->
          <div>
            <div class="bg-gray-50 rounded-2xl p-8">
              <h2 class="text-2xl font-bold text-[#0f4640] mb-6">
                معلومات الاتصال
              </h2>
              <p class="text-gray-600 mb-8">
                لا تتردد في التواصل معنا من خلال أي من هذه القنوات. نحن متاحون لمساعدتك خلال ساعات العمل.
              </p>
              
              <div class="space-y-6">
                <!-- Address -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-[#0f4640]/5 rounded-xl flex items-center justify-center">
                    <i class="fas fa-map-marker-alt text-[#0f4640] text-xl"></i>
                  </div>
                  <div>
                    <h3 class="font-bold text-[#0f4640] mb-1">العنوان</h3>
                    <p class="text-gray-600">123 شارع الأعمال، الرياض، المملكة العربية السعودية</p>
                  </div>
                </div>

                <!-- Phone -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-[#0f4640]/5 rounded-xl flex items-center justify-center">
                    <i class="fas fa-phone-alt text-[#0f4640] text-xl"></i>
                  </div>
                  <div>
                    <h3 class="font-bold text-[#0f4640] mb-1">الهاتف</h3>
                    <p class="text-gray-600 font-arabic" dir="ltr">+966 12 345 6789</p>
                  </div>
                </div>

                <!-- Email -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-[#0f4640]/5 rounded-xl flex items-center justify-center">
                    <i class="fas fa-envelope text-[#0f4640] text-xl"></i>
                  </div>
                  <div>
                    <h3 class="font-bold text-[#0f4640] mb-1">البريد الإلكتروني</h3>
                    <p class="text-gray-600 font-arabic">info@salasah.com</p>
                  </div>
                </div>

                <!-- Hours -->
                <div class="flex items-start gap-4">
                  <div class="flex-shrink-0 w-12 h-12 bg-[#0f4640]/5 rounded-xl flex items-center justify-center">
                    <i class="fas fa-clock text-[#0f4640] text-xl"></i>
                  </div>
                  <div>
                    <h3 class="font-bold text-[#0f4640] mb-1">ساعات العمل</h3>
                    <p class="text-gray-600">الأحد - الخميس: 9:00 صباحاً - 5:00 مساءً</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'ContactView',
  data() {
    return {
      form: {
        name: '',
        email: '',
        phone: '',
        message: ''
      }
    }
  },
  methods: {
    handleSubmit() {
      console.log('Form submitted:', this.form)
    }
  }
}
</script>

<style scoped>
.contact-page {
  min-height: 100vh;
  background-color: white;
}

[dir="rtl"] {
  font-family: 'Cairo', sans-serif;
}

[dir="ltr"] {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Form styles */
input, textarea {
  direction: inherit;
}

[dir="rtl"] input,
[dir="rtl"] textarea {
  font-family: 'Cairo', sans-serif;
}

input:focus, textarea:focus {
  outline: none;
}

/* Button hover effect */
button:hover:not(:disabled) {
  transform: translateY(-2px);
}

button:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Font for English text */
.font-arabic {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
}

/* Hero text animation */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.hero-title {
  position: relative;
  z-index: 1;
  line-height: 1.2;
  margin-bottom: 1rem;
}

.flex-col > * {
  animation: fadeInUp 0.5s ease-out forwards;
}

.flex-col > *:nth-child(1) { animation-delay: 0.1s; }
.flex-col > *:nth-child(2) { animation-delay: 0.2s; }
.flex-col > *:nth-child(3) { animation-delay: 0.3s; }

/* Enhanced spacing for hero section */
.hero-title span {
  display: block;
  line-height: 1.1;
}

[dir="rtl"] .hero-title span:first-child {
  margin-bottom: 0.5rem;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .hero-title span {
    font-size: 4rem;
  }
  
  [dir="rtl"] .hero-title span:first-child {
    font-size: 5rem;
  }
}
</style> 